import type { TempoEvent } from './types';

/**
 * A tempo map from a performance.
 *
 * Pinning a take's beats with warp markers straightens the take to the grid:
 * the audio is stretched, beat by beat, until it sits on a fixed tempo. This
 * is the other answer. The take stays exactly as played, and the *grid* is
 * bent to it: a tempo event at every beat, set so that the grid's beat n falls
 * where the take's beat n was. A live drummer's push and drag becomes the
 * project's tempo map, and every part recorded afterwards follows it.
 *
 * Between two tracked beats the tempo is what those two beats make: the beats
 * between them, over the seconds between them. Because a tempo event at beat
 * n_i and the next at n_{i+1} hold that tempo for exactly n_{i+1} − n_i beats,
 * the grid reaches beat n_{i+1} exactly when the take does — no integral, no
 * drift, each segment closed by construction. The map before the clip is left
 * as it was, which is what keeps the clip's start where it is; the map after
 * the last beat holds the last tempo until whatever event was already there.
 *
 * The lead — the clip's start to its first tracked beat — is the one place
 * with a choice in it. When the first beat falls on or after the first grid
 * beat under the average tempo, the lead becomes a segment of its own, at the
 * tempo that puts that beat on that grid line. When it falls before — the
 * drummer came in inside the first half-beat — there is no beat to hang a
 * segment on, and the caller is told how far to move the clip earlier so its
 * first beat *is* the clip's start.
 */

export interface PerformanceBeat {
  /** Seconds into the clip. */
  sec: number;
  /** The beat of the grid it should land on, counted from the clip's start. */
  number: number;
}

export interface TempoFromBeats {
  tempoMap: TempoEvent[];
  /** Seconds the clip has to move earlier for its first beat to be its start; zero when it need not. */
  moveEarlierSec: number;
}

const MIN_BPM = 5;
const MAX_BPM = 900;

export function tempoMapFromBeats(
  existing: readonly TempoEvent[],
  ppq: number,
  clipStartTicks: number,
  beats: readonly PerformanceBeat[],
): TempoFromBeats | null {
  if (beats.length < 2) return null;
  const first = beats[0];

  // A first beat before the first grid beat cannot be reached by a tempo: the
  // clip moves instead, and the beats are re-read from its new start.
  if (first.number === 0 && first.sec > 0) {
    return { tempoMap: [...existing], moveEarlierSec: first.sec };
  }

  const lastBeatTick = clipStartTicks + beats[beats.length - 1].number * ppq;

  // Everything the performance covers is the performance's to say. Events
  // before the clip stay; the first event at or after the last beat stays too,
  // and the last beat's tempo holds until it.
  const kept = existing.filter((e) => e.tick < clipStartTicks || e.tick > lastBeatTick);
  const events: TempoEvent[] = [];

  const clamp = (bpm: number): number => Math.min(MAX_BPM, Math.max(MIN_BPM, bpm));

  if (first.number > 0) {
    // The lead: `first.number` beats across `first.sec` seconds.
    events.push({ tick: clipStartTicks, bpm: clamp((60 * first.number) / first.sec) });
  }
  for (let i = 0; i + 1 < beats.length; i++) {
    const a = beats[i];
    const b = beats[i + 1];
    const seconds = b.sec - a.sec;
    const count = b.number - a.number;
    if (seconds <= 0 || count <= 0) continue;
    events.push({ tick: clipStartTicks + a.number * ppq, bpm: clamp((60 * count) / seconds) });
  }
  // After the last beat, the tempo the last two beats made.
  const last = events[events.length - 1];
  if (last) events.push({ tick: lastBeatTick, bpm: last.bpm });

  // The clip's start must stay where it is in seconds, which the events before
  // it guarantee, and the map must still begin at tick zero.
  const tempoMap = [...kept, ...events].sort((a, b) => a.tick - b.tick);
  if (tempoMap.length === 0 || tempoMap[0].tick !== 0) {
    tempoMap.unshift({ tick: 0, bpm: tempoMap[0]?.bpm ?? 120 });
  }
  // A held value at the very start of the clip is redundant when an event is
  // already there; the sort put the new one last, and the new one is what we want.
  const deduped: TempoEvent[] = [];
  for (const event of tempoMap) {
    const previous = deduped[deduped.length - 1];
    if (previous && previous.tick === event.tick) deduped[deduped.length - 1] = event;
    else deduped.push(event);
  }
  return { tempoMap: deduped, moveEarlierSec: 0 };
}
